import { Button, Empty, Flex, notification } from "antd";
import dayjs from "dayjs";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
    getApiError,
    getDebtPayments,
    getExportExportPaymentsThunk,
    postInvoiceMakeExportPayment,
} from "tm-shared/api";
import { backendDateFormat } from "tm-shared/constants";
import { MarketDebt } from "tm-shared/types";
import { formatUAHStandard } from "tm-shared/utils";

import { PrimaryText } from "@/components/Typography/Styled";
import { BlockLoader } from "@/components/UI/BlockLoader";
import { isManagerRole } from "@/hooks/isManagerRole";
import { useAppDispatch, useAppSelector } from "@/hooks/redux";
import ExportDropdown from "@/pages/SV/RouteManagement/components/ExportDropdown";
import { StyledSum } from "@/pages/WorkPlan/Tasks/shared";
import { changeOldExportedPaymentsSum } from "@/store/slices/work/work.slice";

import { PaymentHeader } from "./components/PaymentHeader";
import { PaymentItem } from "./components/PaymentItem";
import {
    StyledActionsContainer,
    StyledCollapse,
    StyledPaymentsContainer,
} from "./Styled";

const Payments = ({ userId }: { userId: string }) => {
    const dispatch = useAppDispatch();

    const { oldExportedPaymentsSum, exportIndicator } = useAppSelector(
        (state) => state.work,
    );
    const { role } = useAppSelector((s) => s.user);
    const { activeSchedule } = useAppSelector((s) => s.sv);

    const [paymentExportLoading, setPaymentExportLoading] = useState(false);
    const [dataLoading, setDataLoading] = useState(false);
    const [debtPaymentList, setDebtPaymentList] = useState<MarketDebt[]>([]);

    const allPayments = useMemo(
        () =>
            Object.values(debtPaymentList).flatMap(({ payments }) => payments),
        [debtPaymentList],
    );

    const isAllExported = useMemo(
        () => allPayments.every(({ isExported }) => isExported),
        [allPayments],
    );

    const totalSum = useMemo(
        () =>
            allPayments.reduce(
                (sum, { paidAmount }) => sum + (paidAmount ?? 0),
                0,
            ),
        [allPayments],
    );

    const notMyPaymentsTotalSum = useMemo(
        () =>
            allPayments
                .filter(({ payer }) => payer !== userId)
                .reduce((sum, { paidAmount }) => sum + (paidAmount ?? 0), 0),
        [allPayments, userId],
    );

    const isAlreadyExported =
        oldExportedPaymentsSum === totalSum - notMyPaymentsTotalSum;

    const handleExportPayment = async () => {
        if (isAlreadyExported || isAllExported) {
            notification.error({
                message: "Всі оплати вивантажені, очікуйте на результат",
            });
            return;
        }

        try {
            setPaymentExportLoading(true);

            await postInvoiceMakeExportPayment({
                agentId: isManagerRole(role) ? activeSchedule : userId,
            });

            dispatch(
                changeOldExportedPaymentsSum(totalSum - notMyPaymentsTotalSum),
            );

            notification.success({
                message: "Запит на вивантаження оплат успішно відправлено",
            });
        } catch (error) {
            const { displayMessage } = getApiError(error);

            notification.error({
                message: "Помилка при вивантаженні оплат",
                description: displayMessage,
            });
        } finally {
            setPaymentExportLoading(false);
        }
    };

    useEffect(() => {
        const fetchDebtPayments = async () => {
            try {
                setDataLoading(true);
                const { result } = await getDebtPayments({
                    agentId: userId,
                    targetDate: dayjs().format(backendDateFormat.date),
                });

                setDebtPaymentList(result);
            } catch (error) {
                const { displayMessage } = getApiError(error);

                notification.error({
                    message: "Помилка при завантаженні оплат",
                    description: displayMessage,
                });
            } finally {
                setDataLoading(false);
            }
        };

        fetchDebtPayments();
    }, [dispatch, userId]);

    const exportPaymentFile = useCallback(
        async ({ key }: { key: string }) => {
            switch (key) {
                case "excel": {
                    dispatch(getExportExportPaymentsThunk({ payerId: userId }));
                    notification.info({ message: "Експорт виплат розпочато" });

                    return;
                }

                default: {
                    return;
                }
            }
        },
        [dispatch, userId],
    );

    if (dataLoading)
        return (
            <Flex flex={1} align="center" justify="center">
                <BlockLoader text="Завантаження оплат..." />
            </Flex>
        );

    const showExportBtn = !isAlreadyExported && !isAllExported;

    const collapseItems = Object.values(debtPaymentList).map((debt) => {
        const isDebtPaymentsExported = debt.payments.every(
            ({ isExported }) => isExported,
        );

        return {
            key: debt.market.id,
            label: (
                <PaymentHeader
                    debt={debt}
                    isExported={isDebtPaymentsExported}
                />
            ),
            children: (
                <Flex vertical gap={10}>
                    {debt.payments.map((payment) => (
                        <PaymentItem
                            key={payment.consignmentId}
                            payment={payment}
                            userId={userId}
                        />
                    ))}
                </Flex>
            ),
        };
    });

    return (
        <>
            <StyledActionsContainer>
                <ExportDropdown
                    isLoading={!!exportIndicator}
                    loadingLabel={exportIndicator.toString()}
                    menuItems={[{ key: "excel", label: "Експорт в Excel" }]}
                    onMenuClick={exportPaymentFile}
                />

                {showExportBtn && (
                    <Button
                        type="primary"
                        onClick={handleExportPayment}
                        loading={paymentExportLoading}
                    >
                        Вивантажити оплати
                    </Button>
                )}
            </StyledActionsContainer>

            <StyledPaymentsContainer>
                {!Object.keys(debtPaymentList).length ? (
                    <Empty
                        image={Empty.PRESENTED_IMAGE_SIMPLE}
                        description="Немає оплат"
                    />
                ) : (
                    <>
                        <StyledSum>
                            <PrimaryText $textColor="white">
                                Загальна сума:{" "}
                                <strong>{formatUAHStandard(totalSum)}</strong>
                            </PrimaryText>
                        </StyledSum>

                        <StyledCollapse accordion items={collapseItems} />
                    </>
                )}
            </StyledPaymentsContainer>
        </>
    );
};

export default React.memo(Payments);
